import { z } from 'zod';
import { GENDERS } from '../types/Gender.js';
import { NAME_STATUSES } from '../types/NameStatus.js';
/**
 * One `attestations` row, as the queue shows it: where a source said this and
 * the words it used. A reading two sources agree on carries one of these each,
 * which is the point of showing them.
 */
export const AdminCitationSchema = z.object({
    id: z.number().int().positive(),
    source: z.string().nullable(),
    locator: z.string(),
    excerpt: z.string().nullable(),
});
/** One reading of a name, as the review queue shows it. */
export const AdminMeaningSchema = z.object({
    id: z.number().int().positive(),
    text: z.string(),
    status: z.enum(NAME_STATUSES),
    source: z.string().nullable(),
    /** The row this reading belongs to, and the one publishing it wins. */
    nameId: z.number().int().positive().nullable(),
});
/**
 * One catalogue row inside a cluster. The spelling and gender live on the
 * cluster; what stays here is what the import filed differently row by row.
 *
 * Religion and language are null where the import never recorded one, which is
 * most of the catalogue's languages.
 */
export const AdminClusterMemberSchema = z.object({
    id: z.number().int().positive(),
    religion: z.string().nullable(),
    language: z.string().nullable(),
    status: z.enum(NAME_STATUSES),
    source: z.string().nullable(),
    /** What the import recorded that no column could hold. */
    notes: z.string().nullable(),
    /** Where each source listed this spelling. */
    citations: z.array(AdminCitationSchema),
});
/**
 * A reading with the evidence behind it. The queue carries this; a review's
 * response carries the bare reading, because nothing it changes is evidence.
 */
export const AdminCitedMeaningSchema = AdminMeaningSchema.extend({
    citations: z.array(AdminCitationSchema),
});
/**
 * What an agent last said about a cluster. This is what "reviewed by AI" is:
 * not a flag on a name, but the newest entry an agent wrote in the ledger about
 * anything in that cluster.
 */
export const AdminVerdictSchema = z.object({
    agent: z.string(),
    /** The agent's own 0–100. Read it sceptically; see `abstained`. */
    confidence: z.number().int().min(0).max(100).nullable(),
    note: z.string().nullable(),
    /** It looked and would not decide: below the bar, and worth a person. */
    abstained: z.boolean(),
    /** It was sure, and found the catalogue already right. Needs nobody. */
    unchanged: z.boolean(),
    /**
     * It decided, on a run that was told to write nothing. The catalogue is
     * untouched and the verdict is only an opinion — which is a different thing
     * from both of the above, and the queue must not read it as "changed this".
     */
    considered: z.boolean(),
    at: z.string(),
});
/**
 * A reading an agent said it would write, from a run that was told not to.
 *
 * The verdict above is only ever the *newest* one, which is the right thing for
 * "has an agent looked at this" and useless for the question these answer:
 * several models were asked the same name, and whether they landed on the same
 * meaning is the strongest evidence available that it is right. Judging that is
 * left to a person — deciding by machine whether two Tamil paraphrases mean the
 * same thing is exactly the sort of guess that would quietly mislead.
 */
export const AdminProposalSchema = z.object({
    agent: z.string(),
    confidence: z.number().int().min(0).max(100).nullable(),
    text: z.string(),
    /** False where the agent was below the bar and would not have written it. */
    confident: z.boolean(),
});
export const AdminClusterSchema = z.object({
    id: z.number().int().positive(),
    name: z.string(),
    gender: z.string(),
    members: z.array(AdminClusterMemberSchema),
    /**
     * Every reading across the cluster's rows. A cluster the import filed more
     * than once is where the readings disagree, which is the whole reason to
     * look at it.
     */
    meanings: z.array(AdminCitedMeaningSchema),
    /** Null where no agent has looked at this cluster yet. */
    verdict: AdminVerdictSchema.nullable(),
    /**
     * What agents would have written for it, newest first, one per agent.
     * Empty unless something has been asked without being allowed to write.
     */
    proposals: z.array(AdminProposalSchema),
});
/**
 * A flag that survives the round trip as a query string. `z.coerce.boolean()`
 * cannot: it reads the string "false" as true.
 */
const BooleanParam = z.union([z.boolean(), z.stringbool()]);
export const AGENT_REVIEW_FILTERS = [
    'decided',
    'unsure',
    'unchanged',
    'considered',
    /**
     * Two or more agents said what they would write. Where they landed on the
     * same meaning that is the best evidence the catalogue can offer for a name
     * nobody has written one for — and where they did not, it is a name to
     * leave alone. Both are worth a person's time, which is why the filter is
     * "two of them answered" rather than "two of them agreed".
     */
    'suggested',
    'none',
];
/**
 * What a run did to a cluster, in the words its own report uses — so clicking
 * "412 written" on a run lands on exactly those 412.
 *
 * Read off the ledger rather than stored, because the ledger already says all
 * of it: `written` is the row the agent created, `published` a reading it
 * promoted, `rejected` and `dropped` the two things it can throw away, and
 * `abstained`/`unchanged` the two ways of leaving a cluster alone. Only
 * `failed` needs a table of its own — nothing was judged, so there is no
 * ledger entry to read it from.
 */
export const RUN_OUTCOMES = [
    'written',
    'published',
    'rejected',
    'dropped',
    'abstained',
    'unchanged',
    /** It decided, on a run told to write nothing. An opinion, not an act. */
    'considered',
    /** The agent could not be asked, or could not be read. */
    'failed',
];
export const AdminNamesQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(25),
    status: z.enum(NAME_STATUSES).optional(),
    gender: z.enum(GENDERS).optional(),
    search: z.string().trim().max(255).optional(),
    /**
     * Only clusters the import filed more than once. These are the ones worth
     * a reviewer's time: their readings are the ones that disagree.
     */
    duplicatesOnly: BooleanParam.optional(),
    /**
     * The second pass. `decided` is what an agent changed and a person has not
     * checked; `unsure` is what it looked at and would not decide, which is
     * where a person is worth most; `none` is the backlog no agent has reached.
     */
    agentReview: z.enum(AGENT_REVIEW_FILTERS).optional(),
    /**
     * Only clusters an agent was at most this sure of. A small model's
     * confidence runs high, so this is the dial that finds the ones to re-read.
     */
    maxConfidence: z.coerce.number().int().min(0).max(100).optional(),
    /**
     * Only clusters one review run reached — including the ones it failed on,
     * which are otherwise invisible: a failure writes no verdict, on purpose.
     */
    run: z.coerce.number().int().positive().optional(),
    /** What the run did to them. Ignored without a `run` to ask it about. */
    runOutcome: z.enum(RUN_OUTCOMES).optional(),
});
export const AdminClustersPageSchema = z.object({
    items: z.array(AdminClusterSchema),
    total: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    limit: z.number().int().positive(),
});
/** The id travels in the path, so it arrives as a string whatever sent it. */
export const AdminStatusUpdateSchema = z.object({
    id: z.coerce.number().int().positive(),
    status: z.enum(NAME_STATUSES),
});
/**
 * Every reading a meaning review changed. Publishing one returns the reading it
 * displaced as well: only one reading of a name may be published, so the
 * incumbent goes back to the pool rather than being rejected on its behalf.
 */
export const AdminMeaningsUpdateSchema = z.object({
    meanings: z.array(AdminMeaningSchema),
});
//# sourceMappingURL=AdminNameSchemas.js.map